import { checkSession, login as apiLogin, logout as apiLogout, type LoginResult } from "../auth";
import { loadSave, saveGame, type FleetShipSaveData, type GameSaveData } from "../gameSave";
import { getShip } from "../data/loadShips";
import { migrateRenamedItems } from "../data/marketItems";
import { normalizeBestiary } from "../bestiary";
import { normalizeCombatSnapshot } from "../combatSnapshot";
import type { PlayerShipVariant } from "../types";
import { initialClockState, effectiveNow } from "../gameClock";
import { startingFuelCapacity } from "../fuel";
import { startingProvisionsCapacity } from "../provisions";
import { initialStanding } from "../factionStanding";
import { createCaptainSkills } from "../captainSkills";
import { createCaptainStats, normalizeCaptainStats } from "../captainStats";
import { clearLocalSave, hasLocalSave, readLocalSave, writeLocalSave } from "../localSave";
import { currentMarketEpoch } from "../market/stationMarket";
import { state, type FleetShip, type Screen } from "./state";
import type { ScreenContext } from "./app";

function toSaveData(): GameSaveData {
  return {
    shipName: state.currentShip?.name ?? null,
    shipVariant: state.currentShip?.variant ?? null,
    currentShipInstanceId: state.currentShipInstanceId,
    weaponUpgrades: state.weaponUpgrades,
    shipDamage: state.shipDamage,
    bestiary: state.bestiary,
    loreSeenSystemIds: state.loreSeenSystemIds,
    hasSeenWelcome: state.hasSeenWelcome,
    stationPurchases: state.stationPurchases,
    tradeStandingAwarded: state.tradeStandingAwarded,
    currentSystemId: state.currentSystemId,
    currentLocationId: state.currentLocationId,
    ownedLeases: state.ownedLeases,
    bases: state.bases,
    nodeRegen: state.nodeRegen,
    standing: state.standing,
    captainSkills: state.captainSkills,
    stats: state.stats,
    visitedSystemIds: state.visitedSystemIds,
    detectedHiddenSystemIds: state.detectedHiddenSystemIds,
    lastTopLevelView: state.lastTopLevelView,
    clock: state.clock,
    fuelOnBoard: state.fuelOnBoard,
    fuelCapacity: state.fuelCapacity,
    provisionsOnBoard: state.provisionsOnBoard,
    provisionsCapacity: state.provisionsCapacity,
    provisionsLastUpdatedAt: state.provisionsLastUpdatedAt,
    provisionsDepletedAt: state.provisionsDepletedAt,
    mutinyWarningsIssued: state.mutinyWarningsIssued,
    inEscapePod: state.inEscapePod,
    isLoanerShip: state.isLoanerShip,
    credits: state.credits,
    cargo: state.cargo,
    acceptedMissions: state.acceptedMissions,
    waypointSystemId: state.waypointSystemId,
    combatSnapshot: state.combatSnapshot,
    fleet: state.fleet.map((f) => ({
      id: f.id,
      shipName: f.ship.name,
      shipVariant: f.ship.variant,
      source: f.source,
      stance: f.stance,
      lastUpkeepAt: f.lastUpkeepAt,
      dockedAtKey: f.dockedAtKey
    }))
  };
}

// Rehydrates against the fixed roster (getShip) the same way currentShip
// already does -- a fleet entry whose ship can no longer be resolved
// (never happens in practice, the roster is static) is dropped rather
// than kept as a dangling reference.
function rehydrateFleet(data: FleetShipSaveData[] | undefined): FleetShip[] {
  if (!data) return [];
  return data.flatMap((f) => {
    const ship = getShip(f.shipName, f.shipVariant as PlayerShipVariant);
    return ship
      ? [
          {
            id: f.id,
            ship,
            source: f.source,
            stance: f.stance,
            lastUpkeepAt: f.lastUpkeepAt,
            // Migration: pre-16.72 saves only have dockedAtBaseKey, and
            // its value is already the right shape.
            dockedAtKey: f.dockedAtKey ?? f.dockedAtBaseKey
          }
        ]
      : [];
  });
}

/**
 * Drops station-purchase entries from a market hour that has already passed
 * (16.92).
 *
 * This is what makes the derived market's one piece of stored state
 * self-cleaning. Keys carry their own epoch, so a save left alone overnight
 * comes back with an empty ledger and every station restocked -- no migration,
 * no sweeper, and no unbounded growth from a captain who trades for months.
 *
 * Done on LOAD rather than on save, deliberately: the save is written
 * constantly and read rarely, and a captain who buys at 10:59 and reloads at
 * 11:01 must see a restocked station, which only a load-time check delivers.
 */
function pruneStationPurchases(stored: Record<string, number> | undefined): Record<string, number> {
  if (!stored) return {};
  const epoch = String(currentMarketEpoch(Date.now()));
  const kept: Record<string, number> = {};
  Object.entries(stored).forEach(([key, units]) => {
    if (key.startsWith(`${epoch}|`)) kept[key] = units;
  });
  return kept;
}

function applySaveData(data: GameSaveData): void {
  state.currentShip = data.shipName
    ? (getShip(data.shipName, (data.shipVariant as PlayerShipVariant) ?? "Standard") ?? null)
    : null;
  state.currentShipInstanceId = data.currentShipInstanceId ?? null;
  state.weaponUpgrades = data.weaponUpgrades ?? {};
  state.shipDamage = data.shipDamage ?? {};
  state.bestiary = normalizeBestiary(data.bestiary);
  state.loreSeenSystemIds = data.loreSeenSystemIds ?? [];
  state.hasSeenWelcome = data.hasSeenWelcome ?? false;
  state.stationPurchases = pruneStationPurchases(data.stationPurchases);
  state.tradeStandingAwarded = pruneStationPurchases(data.tradeStandingAwarded);
  state.currentSystemId = data.currentSystemId ?? "S001";
  state.currentLocationId = data.currentLocationId ?? "planet-1";
  state.ownedLeases = data.ownedLeases ?? [];
  // Base storage is item-keyed too, so it needs the same 16.88 rename
  // migration as the cargo hold below -- a captain's stockpiled goods are
  // exactly the kind of thing that sits in a Base for a long time and would
  // otherwise be the last place an orphaned key ever got noticed.
  state.bases = (data.bases ?? []).map((base) => ({
    ...base,
    storage: migrateRenamedItems(base.storage ?? {})
  }));
  state.nodeRegen = data.nodeRegen ?? [];
  state.standing = data.standing ?? initialStanding();
  // Spread over a fresh zeroed set rather than trusting the blob wholesale:
  // a pre-16.69 save has no skills at all, and a save written by a future
  // build that adds an 8th skill would otherwise load it as undefined and
  // NaN every bonus derived from it.
  state.captainSkills = { ...createCaptainSkills(), ...(data.captainSkills ?? {}) };
  state.stats = normalizeCaptainStats(data.stats);
  state.visitedSystemIds = data.visitedSystemIds ?? [];
  state.detectedHiddenSystemIds = data.detectedHiddenSystemIds ?? [];
  state.lastTopLevelView = data.lastTopLevelView ?? null;
  state.clock = data.clock ?? initialClockState(Date.now());
  state.fuelOnBoard = data.fuelOnBoard ?? (state.currentShip ? startingFuelCapacity(state.currentShip.hullTierLabel) : 0);
  state.fuelCapacity = data.fuelCapacity ?? (state.currentShip ? startingFuelCapacity(state.currentShip.hullTierLabel) : 0);
  state.provisionsOnBoard =
    data.provisionsOnBoard ?? (state.currentShip ? startingProvisionsCapacity(state.currentShip.crew) : 0);
  state.provisionsCapacity =
    data.provisionsCapacity ?? (state.currentShip ? startingProvisionsCapacity(state.currentShip.crew) : 0);
  state.provisionsLastUpdatedAt = data.provisionsLastUpdatedAt ?? effectiveNow(state.clock, Date.now());
  state.provisionsDepletedAt = data.provisionsDepletedAt ?? null;
  state.mutinyWarningsIssued = data.mutinyWarningsIssued ?? 0;
  state.inEscapePod = data.inEscapePod ?? false;
  state.isLoanerShip = data.isLoanerShip ?? false;
  state.credits = data.credits ?? 0;
  // 16.88: items renamed for cubic-metre plausibility. Applied here, in the
  // one function every load path funnels through (server sync, file import,
  // local save, and both sides of conflict resolution), rather than at each
  // caller -- one of them would have been missed.
  state.cargo = migrateRenamedItems(data.cargo ?? {});
  state.acceptedMissions = data.acceptedMissions ?? [];
  state.waypointSystemId = data.waypointSystemId ?? null;
  // Coerced rather than trusted: a hand-edited save must degrade to "no fight"
  // instead of putting a NaN-positioned hostile in the Expedition View.
  state.combatSnapshot = normalizeCombatSnapshot(data.combatSnapshot);
  state.fleet = rehydrateFleet(data.fleet);
}

// Export/Import Save (16.44) -- a real local save FILE, distinct from the
// server sync above. Section 13's own local-first model was never fully
// wired in (bootAuth only ever checks the server session, no localStorage
// autosave exists), so this doesn't make Continue/New Game tell a
// returning captain from a new one -- it's the doc's other named use for
// a local save: a portable file the captain can back up or hand between
// browsers/devices, working the same way whether or not they're logged
// in (CLAUDE.md: "the game must be fully playable/saveable via
// localStorage alone" -- a downloaded file is the same idea made
// explicit and portable, rather than an invisible browser-local store).
const SAVE_FILE_SCHEMA = "starforge-save-v1";

interface SaveFileEnvelope {
  schema: string;
  exportedAt: string;
  state: GameSaveData;
}

export function exportSaveToFile(): void {
  const envelope: SaveFileEnvelope = {
    schema: SAVE_FILE_SCHEMA,
    exportedAt: new Date().toISOString(),
    state: toSaveData()
  };
  const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const datePart = envelope.exportedAt.slice(0, 10);
  a.download = `starforge-save-${datePart}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export type ImportSaveResult = { ok: true } | { ok: false; error: string };

// Applies the imported file's state as the new live state and, if
// authenticated, immediately overwrites the server's save with it too --
// "restore from this file" means the file wins, not a merge. Deliberately
// light validation (real top-level fields present, not a full schema
// check) -- this is a captain restoring their OWN exported file, not
// untrusted input from a stranger; the goal is catching "wrong file
// entirely" (a screenshot, some other game's save), not policing shape.
export async function importSaveFromFile(file: File, ctx: ScreenContext): Promise<ImportSaveResult> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    return { ok: false, error: "That file isn't valid JSON." };
  }
  const envelope = parsed as Partial<SaveFileEnvelope>;
  if (envelope.schema !== SAVE_FILE_SCHEMA || !envelope.state || typeof envelope.state !== "object") {
    return { ok: false, error: "That doesn't look like a Starforge save file." };
  }
  applySaveData(envelope.state);
  state.saveNotice = null;
  state.lastEvent = `Imported save from ${envelope.exportedAt ? new Date(envelope.exportedAt).toLocaleString() : "file"}.`;
  // Writes the local save too (and syncs to the server if authenticated)
  // -- an import is exactly the kind of "meaningful state change"
  // triggerSave already covers, regardless of login state (16.57).
  triggerSave(ctx);
  return { ok: true };
}

async function loadExistingSave(): Promise<boolean> {
  const save = await loadSave();
  if (save) {
    applySaveData(save.state);
    state.saveVersion = save.version;
    return true;
  }
  return false;
}

// Save-comparison UI (16.64) -- explicit, captain-driven load of one save
// or the other, distinct from bootAuth's own automatic local-first
// preference. Both are thin wrappers so escapePodScreen-style screens
// never need to reach into applySaveData directly.
export function loadLocalSaveIntoState(): boolean {
  const local = readLocalSave();
  if (!local) return false;
  applySaveData(local.state);
  return true;
}

export async function loadServerSaveIntoState(): Promise<boolean> {
  return loadExistingSave();
}

// Save-conflict resolution (16.69). Both paths adopt the server's version
// number, because that's what makes the NEXT save succeed instead of
// 409-ing again -- the version is a concurrency token, not part of the
// captain's progress, so carrying it over is correct whichever save's
// *data* the captain picked.
export function resolveConflictKeepServer(ctx: ScreenContext): void {
  const conflict = state.saveConflict;
  if (!conflict) return;
  applySaveData(conflict.serverState);
  state.saveVersion = conflict.serverVersion;
  state.saveConflict = null;
  state.saveNotice = null;
  state.lastEvent = "Kept the server save.";
  // Mirror the adopted server state back into the local save so the two
  // agree immediately, rather than leaving a stale local copy that would
  // look like a fresh conflict on the next boot.
  writeLocalSave(toSaveData(), Date.now());
  // resync(), not rerender(): applySaveData just swapped System/Location/
  // cargo/fleet wholesale, and a mounted Expedition View would otherwise
  // keep rendering the old scene against the new state.
  ctx.resync();
}

export function resolveConflictKeepLocal(ctx: ScreenContext): void {
  const conflict = state.saveConflict;
  if (!conflict) return;
  // Live state is already the local candidate -- nothing to apply, just
  // take the server's version token so the retry below overwrites rather
  // than conflicting, and let the normal save path push it up.
  state.saveVersion = conflict.serverVersion;
  state.saveConflict = null;
  state.saveNotice = null;
  state.lastEvent = "Kept this session's save.";
  triggerSave(ctx);
  ctx.rerender();
}

// Called once at app boot. Local-first (16.57): a local save is checked
// FIRST, regardless of login state, and applied immediately -- before the
// session check's network round-trip even resolves -- so Continue can
// resume a returning captain instantly offline (CLAUDE.md: "the game must
// be fully playable/saveable via localStorage alone"). If no local save
// exists but the captain turns out to be authenticated, this falls back
// to the existing server-save load. Deliberately does NOT navigate
// anywhere itself (mainMenuScreen.ts's own Continue/New Game actions do
// that) -- the Main Menu is meant to be a real resting screen the captain
// actually sees (Section 13a's "living attract mode"), not something
// bootAuth auto-skips past the instant this resolves.
//
// Explicitly NOT addressed here (16.57's own scope limit): local-vs-
// server conflict resolution when both exist and disagree -- the
// existing "server wins on conflict" rule (doSave's 409 handling below)
// is unchanged; this only makes boot aware a local save exists at all.
export async function bootAuth(): Promise<void> {
  const local = readLocalSave();
  if (local) {
    applySaveData(local.state);
    state.hasExistingSave = true;
  }

  const session = await checkSession();
  if (!session.loggedIn) {
    state.authStatus = "unauthenticated";
    return;
  }
  state.authStatus = "authenticated";
  state.username = session.username;
  state.displayName = session.displayName;
  if (!local) {
    state.hasExistingSave = await loadExistingSave();
  }
}

// Where Continue/New Game land, given whatever bootAuth already resolved
// -- authenticated with a ship resumes into whichever of the three real
// top-level Views (Expedition/Base/Station, Section 2a) the ship was
// physically in when the browser was last closed/logged out (16.35: "we
// should be brought to either Base View, Station View, or the Expedition
// View depending on which of these we were in"), falling back to the
// Mission Board only if that's never been set (a genuinely fresh save).
// Authenticated with no ship goes to Ship Select; anyone else goes to
// Login. This is exactly the auto-navigate logic bootAuth used to run by
// itself; now it's an explicit action from the Main Menu instead.
function resumeScreen(): Screen {
  // Expedition View is the default landing spot for a returning captain
  // (Don's direction) -- out in space is where the game actually happens.
  // The one exception is baked into lastTopLevelView itself: if they were
  // docked at a Base or Station when they last closed the game, that's
  // what it holds, and that's where they come back to.
  return state.lastTopLevelView ?? "expedition";
}

export function continueIntoGame(ctx: ScreenContext): void {
  // A ship already loaded (from a local OR server save, see bootAuth)
  // resumes directly regardless of login state (16.57) -- this is the
  // concrete fix for a returning-but-unauthenticated captain: local-first
  // means their progress is real and resumable without a network
  // round-trip, not gated on being logged in.
  if (state.currentShip) {
    ctx.navigate(resumeScreen());
    return;
  }
  // Ejected by mutiny (16.62) -- shipless but mid-Escape-Pod, not a fresh
  // captain who's never picked a ship. Resume the Pod, not Ship Select.
  if (state.inEscapePod) {
    ctx.navigate("escapePod");
    return;
  }
  // Neither a local nor a server save exists: "behaves like New Game"
  // (16.57) -- Ship Select, unconditionally. Login is optional by design
  // (Section 13/16.57's own "fully playable via localStorage alone"), so
  // this must never gate on authStatus -- that would strand every
  // logged-out captain with nothing to continue into.
  ctx.navigate("ships");
}

// New Game (16.57): clears the local save and every save-backed field,
// keeping auth/session state untouched (a signed-in captain starting a
// new captain stays signed in) -- distinct from logoutAndReset, which
// also tears down the session itself. Callers (mainMenuScreen.ts) are
// responsible for the overwrite-confirmation dialog before calling this.
export function startNewGame(ctx: ScreenContext): void {
  clearLocalSave();
  state.currentShip = null;
  state.currentShipInstanceId = null;
  state.weaponUpgrades = {};
  state.shipDamage = {};
  state.bestiary = {};
  state.loreSeenSystemIds = [];
  // A new captain is new: the welcome + tutorial should greet them again.
  state.hasSeenWelcome = false;
  state.stationPurchases = {};
  state.tradeStandingAwarded = {};
  state.currentSystemId = "S001";
  state.currentLocationId = "planet-1";
  state.ownedLeases = [];
  state.bases = [];
  state.nodeRegen = [];
  state.standing = initialStanding();
  state.captainSkills = createCaptainSkills();
  state.stats = createCaptainStats();
  state.visitedSystemIds = [];
  state.detectedHiddenSystemIds = [];
  state.lastTopLevelView = null;
  state.clock = initialClockState(Date.now());
  state.fuelOnBoard = 0;
  state.fuelCapacity = 0;
  state.provisionsOnBoard = 0;
  state.provisionsCapacity = 0;
  state.provisionsLastUpdatedAt = 0;
  state.provisionsDepletedAt = null;
  state.mutinyWarningsIssued = 0;
  state.inEscapePod = false;
  state.isLoanerShip = false;
  state.credits = 0;
  state.cargo = {};
  state.acceptedMissions = [];
  state.waypointSystemId = null;
  state.fleet = [];
  state.hasExistingSave = false;
  state.lastEvent = null;
  // Proceeds to Ship Select unconditionally (16.57) -- login is optional
  // by design, so New Game must never strand a logged-out captain on the
  // Login screen instead.
  ctx.navigate("ships");
}

export async function loginAndBoot(username: string, password: string): Promise<LoginResult> {
  const result = await apiLogin(username, password);
  if (result.success) {
    await adoptSession(result.username ?? username);
  }
  return result;
}

/**
 * Brings client state in line with a session the server has already
 * established, and picks the landing screen.
 *
 * Split out of loginAndBoot because verify-email.ashx and
 * reset-password.ashx also sign the player in (design doc 16.90) -- the token
 * proved control of the mailbox, so there is no password step to run. Those
 * flows need everything loginAndBoot does *except* the password call, and
 * sharing this is what keeps them from drifting into a subtly different
 * post-login state.
 */
export async function adoptSession(username: string): Promise<void> {
  state.authStatus = "authenticated";
  state.username = username;

  // verify-email.ashx and reset-password.ashx only hand back the raw
  // account key, not whether a nickname exists -- one more round trip gets
  // the real displayName rather than showing the opaque key for however
  // long until the next unrelated session check happens to correct it.
  const session = await checkSession();
  state.displayName = session.displayName;

  // A local save (if any) already won at bootAuth's own local-first
  // check -- only fall back to the server save here if there's still
  // nothing loaded.
  if (!state.currentShip) {
    state.hasExistingSave = await loadExistingSave();
  }
  state.screen = state.currentShip ? resumeScreen() : state.inEscapePod ? "escapePod" : "ships";
}

export async function logoutAndReset(): Promise<void> {
  await apiLogout();
  state.authStatus = "unauthenticated";
  state.username = null;
  state.displayName = null;
  state.currentShip = null;
  state.currentShipInstanceId = null;
  state.weaponUpgrades = {};
  state.shipDamage = {};
  state.bestiary = {};
  state.loreSeenSystemIds = [];
  // A new captain is new: the welcome + tutorial should greet them again.
  state.hasSeenWelcome = false;
  state.stationPurchases = {};
  state.tradeStandingAwarded = {};
  state.currentSystemId = "S001";
  state.currentLocationId = "planet-1";
  state.ownedLeases = [];
  state.bases = [];
  state.nodeRegen = [];
  state.standing = initialStanding();
  state.captainSkills = createCaptainSkills();
  state.stats = createCaptainStats();
  state.visitedSystemIds = [];
  state.detectedHiddenSystemIds = [];
  state.lastTopLevelView = null;
  state.clock = initialClockState(Date.now());
  state.fuelOnBoard = 0;
  state.fuelCapacity = 0;
  state.provisionsOnBoard = 0;
  state.provisionsCapacity = 0;
  state.provisionsLastUpdatedAt = 0;
  state.provisionsDepletedAt = null;
  state.mutinyWarningsIssued = 0;
  state.inEscapePod = false;
  state.isLoanerShip = false;
  state.credits = 0;
  state.cargo = {};
  state.acceptedMissions = [];
  state.waypointSystemId = null;
  state.fleet = [];
  state.saveVersion = 0;
  state.saveNotice = null;
  state.lastEvent = null;
  // Logging out tears down the session only, not the local save itself
  // (16.57's local-first model: a local save is independent of the server
  // session) -- re-derive whether one's still there rather than assuming.
  state.hasExistingSave = hasLocalSave();
  state.screen = "login";
}

// Saves are serialized through one chain rather than fired independently,
// so a burst of triggerSave() calls from unrelated actions never overlaps
// requests or drops one silently.
let saveChain: Promise<void> = Promise.resolve();

// Save on key state changes (mission accepted/completed, ship selection),
// not only on explicit request -- called from the screens/checks that
// cause those changes. Local-first (16.57): every call writes the local
// save too, regardless of login state -- one extra best-effort
// localStorage.setItem alongside whatever server sync also happens, so
// offline play is always saveable, not just additive on top of a server
// session. Server sync itself stays a no-op while logged out.
export function triggerSave(ctx: ScreenContext): void {
  writeLocalSave(toSaveData(), Date.now());
  state.hasExistingSave = true;
  if (state.authStatus !== "authenticated") {
    return;
  }
  saveChain = saveChain.then(() => doSave(ctx));
}

async function doSave(ctx: ScreenContext): Promise<void> {
  const result = await saveGame(toSaveData(), state.saveVersion);

  if (result.outcome === "saved") {
    state.saveVersion = result.version;
    return;
  }

  // Session expired out from under us (16.74). The client still believed
  // it was signed in, so every subsequent triggerSave kept POSTing and
  // kept getting 401s -- an unbounded stream of console errors and failed
  // requests that could never succeed. Downgrade to unauthenticated so
  // triggerSave's own guard stops the retries at the source, and say it
  // once. Nothing is lost: triggerSave writes the local save BEFORE this
  // ever runs, so play continues offline exactly as 16.57 intends.
  if (result.outcome === "unauthorized") {
    state.authStatus = "unauthenticated";
    state.username = null;
    state.displayName = null;
    state.saveVersion = 0;
    state.saveNotice =
      "Your sign-in expired — still saving locally. Log in again to resume cloud sync.";
    ctx.rerender();
    return;
  }

  if (result.outcome === "conflict") {
    // SUPERSEDED by 16.69 -- this used to silently apply the server's copy
    // over live state ("server wins"), which could destroy a whole session's
    // unsaved play with nothing but a notice banner to show for it. Now the
    // conflict is parked and the captain is sent to Compare Saves to pick,
    // with both saves' timestamp/location visible first.
    //
    // Live state is deliberately left ALONE here: it's one of the two
    // candidates the captain is about to choose between, so overwriting it
    // now would defeat the entire point. No resync() either, for the same
    // reason -- nothing about the current scene has changed yet.
    //
    // A true field-level merge stays explicitly out of scope (16.69): merging
    // two divergent saves per-field can fabricate a state neither actually
    // had (duplicated credits, cargo past hold capacity), so this is a
    // whole-save choice, not a merge.
    state.saveConflict = {
      serverState: result.serverSave.state,
      serverVersion: result.serverSave.version,
      serverUpdatedAt: result.serverSave.updatedAt ?? null
    };
    state.saveNotice =
      "This captain was also played in another session -- choose which save to keep.";
    ctx.navigate("saveCompare");
    return;
  }

  // A failed save changes no game state at all -- only this banner. A
  // plain rerender is deliberate: it must NOT disturb a mounted Expedition
  // View mid-encounter just to surface a message (the Expedition View
  // drains state.lastEvent into its own in-world banner instead, see
  // expeditionScreen.ts's frame loop).
  state.lastEvent = `Save failed: ${result.error}`;
  ctx.rerender();
}

/**
 * Save locally the instant the page is being hidden or torn down (16.99).
 *
 * Don asked that closing the browser mid-fight be survivable. Everything needed
 * was already in `state` -- 16.99's combat snapshot is written continuously by
 * the Expedition View -- but nothing wrote it to disk unless the captain pressed
 * something. There is no periodic autosave, so a closed tab lost the fight.
 *
 * Local only, deliberately. A network POST during unload is unreliable in every
 * browser and would need `fetch(..., {keepalive: true})` plus conflict handling
 * for a write nobody is waiting on; `localStorage.setItem` is synchronous and
 * always completes. The server catches up on the next real save.
 *
 * Both events, because neither alone is enough: `visibilitychange` to hidden is
 * the only one mobile Safari reliably fires, and `pagehide` covers a desktop tab
 * being closed outright.
 */
export function installUnloadSave(): void {
  const save = (): void => {
    // Only when there is something to save. Firing during the login screen would
    // write an empty captain over a real local save.
    if (!state.currentShip) return;
    try {
      writeLocalSave(toSaveData(), Date.now());
    } catch {
      // Nothing useful to do while the page is going away, and an exception here
      // would be invisible anyway.
    }
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") save();
  });
  window.addEventListener("pagehide", save);
}
